"use client"

/**
 * How it works — the five-step operating loop, from buyer question to
 * measured outcome. Runs on the shared marketing shell (SiteHeader /
 * SiteFooter) and the current dark editorial system defined globally in
 * globals.css (flat brand blue, Geist display type) — not the retired
 * old-landing.css cyan scope.
 */

import Link from "next/link"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { SiteHeader } from "@/components/landing/site-header"
import { SiteFooter } from "@/components/landing/site-footer"
import { FadeUp, Stagger, StaggerItem, ScrollProgress } from "@/components/landing/motion-primitives"
import { DEMO_WORKSPACE } from "@/lib/site-config"

export default function HowItWorksPage() {
  return (
    <main className="velova-canvas min-h-screen overflow-x-clip">
      <ScrollProgress />
      <SiteHeader />
      <Hero />
      <Steps />
      <FinalCTA />
      <SiteFooter />
    </main>
  )
}

/* ─── Hero ──────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative pt-14 md:pt-20 pb-16 md:pb-24 overflow-hidden">
      <div aria-hidden className="absolute inset-x-0 top-0 h-[560px] pointer-events-none">
        <div className="velova-brush h-full w-full" />
      </div>

      <div className="relative max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <FadeUp className="flex items-center gap-4 mb-8" y={10}>
          <span className="velova-mono-label opacity-60">From question to growth signal</span>
          <div className="flex-1 h-px bg-[var(--velova-line-strong)]" />
        </FadeUp>

        <FadeUp y={24}>
          <h1 className="velova-display text-[36px] sm:text-[52px] md:text-[68px] lg:text-[80px] leading-[1.04] max-w-4xl">
            One question. One action.{" "}
            <span className="velova-italic" style={{ color: "var(--velova-blue-soft)" }}>
              One measurable loop.
            </span>
          </h1>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-8 max-w-[640px]">
          <p className="text-[16px] sm:text-[18px] leading-[1.6] text-white/65">
            Velova follows the full path from what a buyer asks to what AI answers, what your
            team publishes and what changes in the market afterward.
          </p>
        </FadeUp>

        <FadeUp delay={0.25} className="mt-10 flex flex-wrap gap-3">
          <Link href="/signup" className="velova-btn velova-btn-ink">
            Start free
            <ArrowRight className="w-[14px] h-[14px] velova-btn-arrow" />
          </Link>
          <Link href="/actions" className="velova-btn velova-btn-ghost">
            Explore Actions
            <ArrowRight className="w-[14px] h-[14px] velova-btn-arrow" />
          </Link>
        </FadeUp>
      </div>
    </section>
  )
}

/* ─── The five steps ────────────────────────────────────────── */
interface Step {
  num: string
  eyebrow: string
  headline: string
  body: string
  labels: string[]
  result: string
  example?: string
}

const STEPS: Step[] = [
  {
    num: "01",
    eyebrow: "SET UP",
    headline: "Start with the buying decision, not a keyword dump.",
    body: "Add the brand, product, market, language and competitors. Velova builds an initial set of discovery, comparison and trust questions. Keep the recommendations, edit them or add the questions your team already cares about.",
    labels: ["Brand", "Market", "Language", "Competitors", "Buyer questions"],
    result: "A prompt set organized around how customers actually choose.",
    example: DEMO_WORKSPACE.buyerQuestion,
  },
  {
    num: "02",
    eyebrow: "MONITOR",
    headline: "Run the questions across the AI surfaces that matter.",
    body: "Velova monitors every tracked question across the answer surfaces included in the plan. The workspace captures the response, brand mentions, position, competitors, sentiment and citations in one view.",
    labels: ["Answer", "Visibility", "First choice", "Sentiment", "Citations"],
    result: "A daily operating picture of AI-search performance.",
  },
  {
    num: "03",
    eyebrow: "DIAGNOSE",
    headline: "See the gap behind the score.",
    body: "Compare the brands, claims, pages and source types around each answer. Velova groups the gaps, ranks the opportunities and shows where content, technical work or external coverage can make the biggest difference.",
    labels: ["Competitor gap", "Source gap", "Content gap", "Technical gap", "Priority"],
    result: "A ranked action plan instead of a list of observations.",
  },
  {
    num: "04",
    eyebrow: "ACT",
    headline: "Create, publish and reach out from the same workflow.",
    body: "Generate the article, comparison page, FAQ, schema, refresh or outreach tied to the gap. Refine it with the team, choose the destination and publish. The action remains connected to the prompt that created it.",
    labels: ["Create", "Edit", "Approve", "Publish", "Outreach"],
    result: "The work goes live without rebuilding the brief in another tool.",
  },
  {
    num: "05",
    eyebrow: "MEASURE",
    headline: "The graph moves or it does not. Either way, you know.",
    body: "Velova runs the questions again and compares the answer over time. Connect GSC and GA to see whether visibility movement reaches search demand, traffic, conversions and revenue.",
    labels: ["Before", "Action published", "After", "Traffic", "Conversion", "Revenue"],
    result: "A repeatable view of what worked, what did not and what to do next.",
  },
]

function Steps() {
  return (
    <section className="py-16 sm:py-24 md:py-28 border-t border-[var(--velova-line-strong)]">
      <div className="max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-16 sm:gap-20 md:gap-24">
          {STEPS.map((step) => (
            <StepRow key={step.num} step={step} />
          ))}
        </div>
      </div>
    </section>
  )
}

function StepRow({ step }: { step: Step }) {
  return (
    <div className="relative">
      <span
        aria-hidden
        className="hidden sm:block pointer-events-none select-none absolute -top-6 md:-top-10 left-0 velova-display text-[120px] md:text-[160px] leading-none opacity-[0.05]"
      >
        {step.num}
      </span>

      <Stagger className="relative grid grid-cols-12 gap-8 md:gap-14 items-start pt-2 sm:pt-10 md:pt-14">
        <StaggerItem className="col-span-12 md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="velova-mono-label" style={{ color: "var(--velova-blue-soft)" }}>
              {step.num} · {step.eyebrow}
            </span>
          </div>
          <h3 className="velova-display text-[26px] sm:text-[32px] md:text-[36px] leading-[1.1] mb-5">
            {step.headline}
          </h3>
          <p className="text-[15px] sm:text-[16px] leading-[1.65] text-white/65 mb-6">
            {step.body}
          </p>
          <div
            className="flex items-start gap-3 pl-4 border-l-2"
            style={{ borderColor: "var(--velova-blue)" }}
          >
            <p
              className="text-[14px] sm:text-[15px] leading-[1.6]"
              style={{ color: "var(--velova-blue-soft)" }}
            >
              {step.result}
            </p>
          </div>
        </StaggerItem>

        <StaggerItem className="col-span-12 md:col-span-7">
          <InterfacePanel step={step} />
        </StaggerItem>
      </Stagger>
    </div>
  )
}

function InterfacePanel({ step }: { step: Step }) {
  return (
    <div className="velova-block p-6 sm:p-7">
      <div className="flex items-center justify-between mb-6">
        <span className="text-[13px] text-white/80 font-medium">{step.eyebrow}</span>
        <span className="velova-mono-label opacity-40">Workspace</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {step.labels.map((label) => (
          <span
            key={label}
            className="text-[12.5px] leading-none px-3 py-2 rounded-full border text-white/75"
            style={{ borderColor: "var(--velova-line-strong)", background: "rgba(255,255,255,0.02)" }}
          >
            {label}
          </span>
        ))}
      </div>

      {step.example && (
        <div
          className="mb-6 p-3.5 rounded-[var(--velova-radius-sm)] border text-[13px] leading-[1.5] text-white/70"
          style={{ borderColor: "var(--velova-line)", background: "var(--velova-paper-2)" }}
        >
          <span className="text-white/45">{DEMO_WORKSPACE.brand} · </span>
          &ldquo;{step.example}&rdquo;
        </div>
      )}

      <div
        className="pt-4 border-t text-[11px] text-white/35"
        style={{ borderColor: "var(--velova-line)" }}
      >
        {DEMO_WORKSPACE.frameLabel}
      </div>
    </div>
  )
}

/* ─── Final CTA ─────────────────────────────────────────────── */
function FinalCTA() {
  return (
    <section
      className="relative py-20 sm:py-28 md:py-32 border-t overflow-hidden"
      style={{ borderColor: "var(--velova-line-strong)" }}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="velova-brush h-full w-full opacity-60" />
      </div>

      <div className="relative max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <FadeUp y={24} className="max-w-3xl">
          <h2 className="velova-display text-[34px] sm:text-[48px] md:text-[60px] leading-[1.06]">
            Start with the answer your buyer sees today.
          </h2>
          <p className="mt-6 text-[16px] sm:text-[18px] leading-[1.6] text-white/65 max-w-xl">
            Add the brand and let Velova turn the first missed recommendation into the first
            action.
          </p>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-10 flex flex-wrap gap-3">
          <Link href="/signup" className="velova-btn velova-btn-ink">
            Start free
            <ArrowUpRight className="w-[14px] h-[14px] velova-btn-arrow" />
          </Link>
          <Link href="/pricing" className="velova-btn velova-btn-ghost">
            View pricing
          </Link>
        </FadeUp>
      </div>
    </section>
  )
}
